import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PhoneIcon from "@mui/icons-material/Phone";
import VideocamIcon from "@mui/icons-material/Videocam";
import LaptopIcon from "@mui/icons-material/Laptop";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ReplayIcon from "@mui/icons-material/Replay";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GlassCard from "../../../components/common/GlassCard";

function getEncounterTypeColor(type) {
  const t = (type || "").toLowerCase();
  if (t.includes("ccm")) return "primary";
  if (t.includes("rpm")) return "secondary";
  if (t.includes("bhi")) return "warning";
  if (t.includes("pcm")) return "info";
  if (t.includes("phone")) return "default";
  if (t.includes("emergency")) return "error";
  return "default";
}

function getEncounterIcon(type) {
  const t = (type || "").toLowerCase();
  if (t.includes("phone")) return <PhoneIcon sx={{ fontSize: 14 }} />;
  if (t.includes("video") || t.includes("telehealth")) return <VideocamIcon sx={{ fontSize: 14 }} />;
  if (t.includes("portal")) return <LaptopIcon sx={{ fontSize: 14 }} />;
  return <EventNoteIcon sx={{ fontSize: 14 }} />;
}

function isAutomated(provider) {
  return provider && (provider.includes("n8n") || provider.includes("automation"));
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function EncountersCard({ encounters, activeSessions, onAddEncounter, onOpenSession }) {
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <GlassCard
      title="Encounters"
      icon={<LocalHospitalIcon color="primary" />}
      onAdd={onAddEncounter}
      addLabel="Add Encounter"
      data-testid="encounters-card"
      data-card-category="encounters"
    >
      {activeSessions?.length > 0 && (
        <Box sx={{ mb: 1 }} data-testid="active-encounter-sessions">
          {activeSessions.map((session) => (
            <Box
              key={`session-${session.id}`}
              role={onOpenSession ? "button" : undefined}
              tabIndex={onOpenSession ? 0 : undefined}
              onClick={onOpenSession ? () => onOpenSession(session) : undefined}
              onKeyDown={
                onOpenSession
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpenSession(session);
                      }
                    }
                  : undefined
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
                p: 0.75,
                mb: 0.5,
                border: "1px dashed",
                borderColor: "warning.main",
                borderRadius: 1,
                cursor: onOpenSession ? "pointer" : "default",
                "&:hover": onOpenSession ? { bgcolor: "action.hover" } : undefined,
              }}
            >
              <Chip label="In progress" size="small" color="warning" sx={{ fontSize: 11, height: 22 }} />
              <Chip
                icon={getEncounterIcon(session.encounter_mode)}
                label={(session.program_type || session.encounter_mode || "Encounter").toString().toUpperCase()}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 22 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                Started {formatDate(session.started_at)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {encounters?.length ? (
        encounters.map((enc, idx) => (
          <Box key={enc.id || idx}>
            {idx > 0 && <Divider sx={{ my: 1 }} />}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover", borderRadius: 1 },
                p: 0.75,
                mx: -0.75,
              }}
              onClick={() => toggleExpand(enc.id)}
            >
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                  <Chip
                    icon={getEncounterIcon(enc.encounter_type)}
                    label={enc.encounter_type || "Encounter"}
                    size="small"
                    color={getEncounterTypeColor(enc.encounter_type)}
                    variant="outlined"
                    sx={{ fontSize: 11, height: 22 }}
                  />
                  {isAutomated(enc.provider) && (
                    <Tooltip title="AI-documented via n8n">
                      <SmartToyIcon sx={{ fontSize: 14, color: "secondary.main" }} />
                    </Tooltip>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                    {formatDate(enc.encounter_date)}
                  </Typography>
                </Box>

                <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                  {enc.chief_complaint || "No chief complaint"}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {enc.provider || "Unknown provider"}
                </Typography>
              </Box>

              <IconButton size="small" aria-label={expandedId === enc.id ? "Collapse encounter" : "Expand encounter"} sx={{ mt: 0.25 }}>
                {expandedId === enc.id ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Box>

            <Collapse in={expandedId === enc.id}>
              <Box sx={{ pl: 1, pt: 1, pb: 0.5 }}>
                {enc.diagnosis && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      SUMMARY / DIAGNOSIS
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: "pre-wrap" }}>
                      {enc.diagnosis}
                    </Typography>
                  </Box>
                )}
                {enc.treatment_plan && (
                  <Box>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      TREATMENT PLAN / NEXT STEPS
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: "pre-wrap" }}>
                      {enc.treatment_plan}
                    </Typography>
                  </Box>
                )}
                {enc.coordinator_next_steps && enc.coordinator_next_steps.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" fontWeight={600} color="secondary.main" display="flex" alignItems="center" gap={0.5}>
                      <ArrowForwardIcon sx={{ fontSize: 12 }} />
                      COORDINATOR NEXT STEPS
                    </Typography>
                    <List dense disablePadding sx={{ mt: 0.25 }}>
                      {enc.coordinator_next_steps.map((step, i) => (
                        <ListItem key={i} disableGutters sx={{ py: 0, minHeight: 24 }}>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <ArrowForwardIcon sx={{ fontSize: 10, color: "secondary.main" }} />
                          </ListItemIcon>
                          <ListItemText primary={step} primaryTypographyProps={{ variant: "body2", fontSize: 12 }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                {enc.carried_from_previous && (
                  <Tooltip title="This encounter's agenda included items from a previous call">
                    <Chip
                      icon={<ReplayIcon sx={{ fontSize: 12 }} />}
                      label="↩ Carried from previous call"
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ mt: 1, fontSize: 10, height: 20 }}
                    />
                  </Tooltip>
                )}
                {!enc.diagnosis && !enc.treatment_plan && !(enc.coordinator_next_steps?.length) && (
                  <Typography variant="caption" color="text.secondary">
                    No additional details recorded.
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        ))
      ) : activeSessions?.length ? null : (
        <Typography variant="body2" color="text.secondary">
          No encounters recorded
        </Typography>
      )}
    </GlassCard>
  );
}

EncountersCard.propTypes = {
  activeSessions: PropTypes.array,
  onOpenSession: PropTypes.func,
  encounters: PropTypes.array,
  onAddEncounter: PropTypes.func,
};

export default EncountersCard;
